import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { store } from "@/lib/storage";
import { isTtsSupported } from "@/lib/tts";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import {
  isNotificationSupported, requestPermission, getPermission,
  getReminderConfig, setReminderConfig, startScheduler, stopScheduler
} from "@/lib/reminders";
import { toast } from "sonner";
import { Bell, Mic, Radio, Crown, LogOut, User as UserIcon } from "lucide-react";

export default function Settings() {
  const nav = useNavigate();
  const { user, entitlement, config, logout } = useAuth();
  const [confirm, setConfirm] = useState(false);
  const [prefs, setPrefs] = useState(store.getPrefs());
  const [reminder, setReminder] = useState(getReminderConfig());
  const [perm, setPerm] = useState(getPermission());
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => setPerm(getPermission()), []);

  async function openPortal() {
    if (portalBusy) return;
    setPortalBusy(true);
    try {
      const { data } = await api.post("/subscription/portal", { return_url: window.location.origin + "/reglages" });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Portail indisponible.");
      setPortalBusy(false);
    }
  }

  function updatePref(patch) {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    store.setPrefs(next);
  }

  async function toggleReminder(v) {
    if (v && perm !== "granted") {
      const res = await requestPermission();
      setPerm(res);
      if (res !== "granted") {
        toast.error("Autorisation refusée par le navigateur.");
        return;
      }
    }
    const next = { ...reminder, enabled: v };
    setReminder(next);
    setReminderConfig(next);
    if (v) { startScheduler(); toast.success("Rappel doux quotidien activé."); }
    else { stopScheduler(); toast.success("Rappel désactivé."); }
  }

  function changeTime(t) {
    const next = { ...reminder, time: t };
    setReminder(next);
    setReminderConfig(next);
  }

  function clearAll() {
    store.clearAll();
    setConfirm(false);
    toast.success("Toutes vos données locales ont été supprimées.");
  }

  return (
    <div className="animate-fade-in pt-6" data-testid="settings-page">
      <h1 className="font-serif text-3xl text-[color:var(--ivoire)]">Réglages</h1>
      <p className="text-sm text-[color:var(--ivoire-muted)] mt-2">
        Audio, voix, affichage, rappels et gestion des données.
      </p>

      <section className="mt-8">
        <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--sauge-light)] mb-3">Audio & voix</p>

        <ToggleRow
          testid="setting-tts"
          icon={<Mic size={16} className="text-[color:var(--sauge-light)]" />}
          title="Voix guidée en français"
          subtitle={isTtsSupported() ? "Lit les instructions à voix haute pendant les exercices." : "Non prise en charge par ce navigateur."}
          value={prefs.tts}
          disabled={!isTtsSupported()}
          onChange={(v) => updatePref({ tts: v })}
        />

        <ToggleRow
          testid="setting-recorded"
          icon={<Radio size={16} className="text-[color:var(--sauge-light)]" />}
          title="Enregistrements haute qualité"
          subtitle="Utilise des enregistrements naturels quand ils sont disponibles."
          value={prefs.preferRecorded}
          onChange={(v) => updatePref({ preferRecorded: v })}
        />
      </section>

      <section className="mt-8">
        <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--sauge-light)] mb-3">Rappel doux quotidien</p>
        {!isNotificationSupported() ? (
          <p className="text-xs text-[color:var(--ivoire-muted)]">Les notifications ne sont pas prises en charge sur ce navigateur.</p>
        ) : (
          <>
            <ToggleRow
              testid="setting-reminder"
              icon={<Bell size={16} className="text-[color:var(--sauge-light)]" />}
              title="Un rappel discret par jour"
              subtitle="Une seule notification, sans son, à l'heure choisie."
              value={reminder.enabled}
              onChange={toggleReminder}
            />
            {reminder.enabled && (
              <div className="mt-3 flex items-center gap-3 rounded-xl border border-[color:var(--border)] bg-[#142235]/50 px-4 py-3">
                <label htmlFor="reminder-time" className="text-sm text-[color:var(--ivoire)]">Heure</label>
                <input
                  id="reminder-time"
                  data-testid="reminder-time"
                  type="time"
                  value={reminder.time}
                  onChange={(e) => changeTime(e.target.value)}
                  className="ml-auto bg-transparent text-[color:var(--ivoire)] font-mono text-sm border border-[color:var(--border)] rounded-md px-2 py-1"
                />
              </div>
            )}
            <p className="mt-2 text-[11px] text-[color:var(--ivoire-subtle)] leading-relaxed">
              Le rappel s'affiche à l'heure choisie quand l'application est ouverte, ou dès sa prochaine ouverture.
            </p>
          </>
        )}
      </section>

      <section className="mt-10 space-y-3">
        <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--sauge-light)] mb-1">Confidentialité</p>
        <InfoRow label="Publicité" value="Aucune." />
        <InfoRow label="Micro et localisation" value="Non utilisés." />
        <InfoRow label="Vos données" value="Restent sur cet appareil et, si vous êtes connecté, sur votre compte." />
      </section>

      <section className="mt-10">
        <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--sauge-light)] mb-3">Mes données</p>
        {!confirm ? (
          <button
            data-testid="clear-data-btn"
            onClick={() => setConfirm(true)}
            className="w-full h-12 rounded-xl border border-[color:var(--border)] text-[color:var(--ivoire)] hover:bg-[#1B2A3E]"
          >
            Effacer mes données locales
          </button>
        ) : (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
            <p className="text-sm text-amber-100">Cette action supprime préférences, favoris et historique de cet appareil. Confirmer ?</p>
            <div className="mt-3 flex gap-2">
              <button
                data-testid="clear-cancel-btn"
                onClick={() => setConfirm(false)}
                className="flex-1 h-11 rounded-lg border border-[color:var(--border)] text-[color:var(--ivoire)]"
              >Annuler</button>
              <button
                data-testid="clear-confirm-btn"
                onClick={clearAll}
                className="flex-1 h-11 rounded-lg bg-red-500/80 text-white font-medium"
              >Confirmer</button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function ToggleRow({ testid, icon, title, subtitle, value, onChange, disabled }) {
  return (
    <button
      data-testid={testid}
      onClick={() => !disabled && onChange(!value)}
      disabled={disabled}
      className={`w-full text-left rounded-xl border border-[color:var(--border)] bg-[#142235]/50 px-4 py-3 mb-2 flex items-start gap-3 transition ${disabled ? "opacity-50" : "hover:bg-[#1B2A3E]"}`}
    >
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[color:var(--ivoire)] font-medium">{title}</div>
        <div className="text-[11px] text-[color:var(--ivoire-muted)] mt-0.5 leading-relaxed">{subtitle}</div>
      </div>
      <span
        aria-hidden
        className={`w-10 h-6 rounded-full relative transition ${value ? "bg-[color:var(--sauge)]" : "bg-[color:var(--bg-surface)]"}`}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 rounded-full bg-[color:var(--ivoire)] transition-all ${value ? "left-4" : "left-0.5"}`}
        />
      </span>
    </button>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-start justify-between rounded-xl border border-[color:var(--border)] bg-[#142235]/50 px-4 py-3">
      <span className="text-sm text-[color:var(--ivoire)]">{label}</span>
      <span className="text-xs text-[color:var(--ivoire-muted)] max-w-[60%] text-right">{value}</span>
    </div>
  );
}
